import { useCallback, useContext, useMemo, useState } from "react";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { Task, TaskGroupBy } from "@common/types/task";
import { TaskListQuery } from "@common/contracts/task.contract";
import { Close } from "@common/schemas/close.schema";
import { NestedTaskRow } from "./types";
import {
    getTaskGroupId,
    getAllPossibleGroupKeys,
    getGroupTitleForKey,
    NO_CLOSE_KEY,
    shouldHideEmptyTaskGroups,
} from "./taskListUtils";
import {
    buildUnsavedTask,
    getPlaceholderTaskFields,
    type TaskPlaceholderFilters,
} from "./taskPlaceholderFields";
import { CloseGroupTitle } from "./TaskView/Cells/CloseGroupTitle";
import {
    getCloseClientName,
    TaskClientNamesContext,
} from "./taskClientNamesContext";

interface BuildPlaceholderParams {
    groupKey: string;
    groupBy: TaskGroupBy;
    isGroupedByCloseStage: boolean;
    focusedGroupKey: string | null;
    sampleTask: Task | undefined;
    handleSetFocusedGroup: (groupKey: string | null) => void;
    handleRemovePlaceholder: () => void;
    filters: TaskPlaceholderFilters;
}

function buildPlaceholderTask({
    groupKey,
    groupBy,
    isGroupedByCloseStage,
    focusedGroupKey,
    sampleTask,
    handleSetFocusedGroup,
    handleRemovePlaceholder,
    filters,
}: BuildPlaceholderParams) {
    return {
        ...buildUnsavedTask({
            id: `new-${groupKey}`,
            fields: getPlaceholderTaskFields({
                groupKey,
                groupBy,
                isGroupedByCloseStage,
                sampleTask,
                filters,
            }),
        }),
        isGroup: false,
        isNewPlaceholder: true,
        groupKey,
        shouldAutoFocus: focusedGroupKey === groupKey,
        onSetFocusedGroup: handleSetFocusedGroup,
        removePlaceholder: handleRemovePlaceholder,
    };
}

type TaskRow = Task & { isNewPlaceholder: boolean; isGroup: boolean };

const taskRows = new WeakMap<Task, TaskRow>();

/**
 * One row object per task object, so regrouping keeps unchanged rows equal
 * and the list's memoized rows skip re-rendering.
 */
const toTaskRow = (task: Task): TaskRow => {
    const cached = taskRows.get(task);
    if (cached) {
        return cached;
    }
    const row = { ...task, isNewPlaceholder: false, isGroup: false };
    taskRows.set(task, row);
    return row;
};

interface UseGroupedTasksParams {
    tasks: Task[];
    groupBy: TaskGroupBy;
    isGroupedByCloseStage: boolean;
    filters: TaskListQuery;
    workspaceMemberIds: number[];
    workspaceMembersMap: Map<number, string>;
    workspaceTaskGroupIds: string[];
    workspaceTaskGroupsMap: Map<string, string>;
    closeStageIds: string[];
    closeStagesMap: Map<string, string>;
    closeIds: string[];
    closesMap: Map<string, Close>;
    entitiesNameMap: Map<number, string>;
    canAddPlaceholders?: boolean;
}

export function useGroupedTasks({
    tasks,
    groupBy,
    isGroupedByCloseStage,
    filters,
    workspaceMemberIds,
    workspaceMembersMap,
    workspaceTaskGroupIds,
    workspaceTaskGroupsMap,
    closeStageIds,
    closeStagesMap,
    closeIds,
    closesMap,
    entitiesNameMap,
    canAddPlaceholders = true,
}: UseGroupedTasksParams) {
    const [groupsWithPlaceholders, setGroupsWithPlaceholders] = useState<
        string | undefined
    >("");
    const [focusedGroupKey, setFocusedGroupKey] = useState<string | null>(null);
    const { refuseIfViewOnly } = useViewOnlyRefusal();
    const clientNamesById = useContext(TaskClientNamesContext);

    const handleAddPlaceholder = useCallback(
        (groupKey: string) => {
            // The placeholder is a client-side row leading to a create the
            // server can only refuse on save: a view-only user would type a
            // title and watch it vanish (PRO-11345).
            if (refuseIfViewOnly()) {
                return;
            }
            setGroupsWithPlaceholders(groupKey);
            setFocusedGroupKey(groupKey);
        },
        [refuseIfViewOnly],
    );

    const handleRemovePlaceholder = useCallback(() => {
        setGroupsWithPlaceholders(undefined);
    }, []);

    const groupedTasks = useMemo(() => {
        const allGroupKeys = getAllPossibleGroupKeys({
            groupBy,
            statusFilter: filters.status,
            workspaceMemberIds,
            taskGroupIds: workspaceTaskGroupIds,
            closeStageIds,
            closeIds,
            clientsMap: clientNamesById,
        });
        const placeholderFilters: TaskPlaceholderFilters = {
            assigneeIds: filters.assigneeIds,
            closeIds: filters.closeIds,
            entityIds: filters.entityIds,
        };

        const nestedData: NestedTaskRow[] = allGroupKeys.map((groupKey) => {
            const matchingTasks = tasks.filter(
                (task) => getTaskGroupId(groupBy, task) === groupKey,
            );
            const sampleTask = matchingTasks[0];
            const showPlaceholder = groupsWithPlaceholders === groupKey;

            const closeForGroup =
                groupBy === TaskGroupBy.CLOSE && groupKey !== NO_CLOSE_KEY
                    ? closesMap.get(groupKey)
                    : undefined;
            const titleNode =
                closeForGroup !== undefined ? (
                    <CloseGroupTitle
                        close={closeForGroup}
                        entityName={
                            entitiesNameMap.get(closeForGroup.entityId) ??
                            getCloseClientName(
                                closeForGroup,
                                clientNamesById,
                            ) ??
                            null
                        }
                    />
                ) : undefined;

            return {
                id: groupKey,
                title: getGroupTitleForKey({
                    groupBy,
                    groupKey,
                    workspaceMembersMap,
                    taskGroupsMap: workspaceTaskGroupsMap,
                    closeStagesMap,
                    closesMap,
                    clientsMap: clientNamesById,
                }),
                titleNode,
                groupBy,
                isGroup: true,
                isNewPlaceholder: false,
                onAddPlaceholder:
                    !isGroupedByCloseStage && canAddPlaceholders
                        ? handleAddPlaceholder
                        : undefined,
                tasks: [
                    ...(showPlaceholder
                        ? [
                              buildPlaceholderTask({
                                  groupKey,
                                  groupBy,
                                  isGroupedByCloseStage,
                                  focusedGroupKey,
                                  sampleTask,
                                  handleSetFocusedGroup: setFocusedGroupKey,
                                  handleRemovePlaceholder,
                                  filters: placeholderFilters,
                              }),
                          ]
                        : []),
                    ...matchingTasks.map(toTaskRow),
                ],
            };
        });

        const shouldHideEmptyGroups = shouldHideEmptyTaskGroups(groupBy);

        return nestedData.filter(
            (group) => !shouldHideEmptyGroups || group.tasks.length > 0,
        );
    }, [
        tasks,
        groupBy,
        isGroupedByCloseStage,
        canAddPlaceholders,
        groupsWithPlaceholders,
        handleAddPlaceholder,
        setFocusedGroupKey,
        focusedGroupKey,
        filters.status,
        filters.assigneeIds,
        filters.closeIds,
        filters.entityIds,
        handleRemovePlaceholder,
        workspaceMemberIds,
        workspaceMembersMap,
        workspaceTaskGroupIds,
        workspaceTaskGroupsMap,
        closeStageIds,
        closeStagesMap,
        closeIds,
        closesMap,
        entitiesNameMap,
        clientNamesById,
    ]);

    return {
        groupedTasks,
        handleAddPlaceholder,
        handleRemovePlaceholder,
        handleSetFocusedGroup: setFocusedGroupKey,
    };
}
